import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { colors, spacing } from '@/theme/tokens';

/**
 * Human fallback for account linking: when auto-match / PESEL can't settle it,
 * the office gets a pool lead ("Przypisanie konta aplikacji") and links the
 * account manually in the CRM. Deliberately a short form — the verified phone
 * travels with the lead automatically.
 */
export default function AuthHelpScreen() {
  const { t } = useTranslation();
  const requestLinkHelp = useAuthStore((s) => s.requestLinkHelp);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [otherPhone, setOtherPhone] = useState('');
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    const result = await requestLinkHelp({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      otherPhone: otherPhone.trim() || null,
      hint: hint.trim() || null,
    });
    setBusy(false);
    if (result.ok) {
      setSent(true);
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  if (sent) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
        <View style={{ padding: spacing.screen, gap: spacing.md, alignItems: 'center', marginTop: spacing.xl }}>
          <Ionicons name="checkmark-circle" size={64} color={colors.primary} />
          <AppText variant="headlineMd" color={colors.navy} style={{ textAlign: 'center' }}>
            {t('auth.helpSentTitle')}
          </AppText>
          <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
            {t('auth.helpSentBody')}
          </AppText>
          <PrimaryButton
            label={t('auth.helpSentDone')}
            onPress={() => router.dismissTo('/menu')}
            style={{ alignSelf: 'stretch' }}
          />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('auth.helpTitle')} />

        <AppText variant="bodyLg">{t('auth.helpIntro')}</AppText>

        <Card style={{ gap: spacing.md }}>
          <TextField label={t('auth.helpFirstName')} value={firstName} onChangeText={setFirstName} />
          <TextField label={t('auth.helpLastName')} value={lastName} onChangeText={setLastName} />
          <TextField
            label={t('auth.helpOtherPhone')}
            value={otherPhone}
            onChangeText={setOtherPhone}
            keyboardType="phone-pad"
            placeholder={t('auth.optional')}
          />
          <TextField
            label={t('auth.helpHint')}
            value={hint}
            onChangeText={setHint}
            placeholder={t('auth.helpHintPlaceholder')}
          />
        </Card>

        {error && (
          <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
            {error}
          </AppText>
        )}

        <PrimaryButton
          label={t('auth.helpSubmit')}
          disabled={busy || !firstName.trim() || !lastName.trim()}
          onPress={onSubmit}
        />
      </ScrollView>
    </SafeAreaView>
  );
}
